const PERFORMANCE_BANDS = [
  {
    min: 90,
    label: "Excellent",
    message: "Outstanding work. Your writing shows confident control of this lesson’s skill.",
  },
  {
    min: 80,
    label: "Very good",
    message: "Your writing is working well. One focused improvement could make it even stronger.",
  },
  {
    min: 65,
    label: "Good",
    message: "You achieved this lesson’s goal. Keep building on this foundation.",
  },
  {
    min: 0,
    label: "Developing",
    message: "You’re building this skill. Use the Coach’s feedback to strengthen your next draft.",
  },
];

const LESSON_ID_FOCUS_FALLBACKS = {
  W1L1: "writing",
  W1L2: "flow",
  W1L3: "sensory detail",
  W1L4: "emotion",
  W2L1: "writing",
  W2L2: "emotion",
  W2L3: "dialogue",
  W2L4: "writing",
  W3L1: "story structure",
  W3L2: "emotion",
  W3L3: "pacing",
  W3L4: "emotion",
  W4L1: "revision",
  W4L2: "rhythm",
  W4L3: "revision",
  W4L4: "emotion",
};

const FOCUS_PATTERNS = [
  { pattern: /(five senses|sensory|sensation|scent|sound|taste|touch|vivid)/i, focus: "sensory detail" },
  { pattern: /(freewrite|freewriting|unfiltered|raw ideas|flow|momentum)/i, focus: "flow" },
  { pattern: /(dialogue|conversation|subtext|speaker|what is said)/i, focus: "dialogue" },
  { pattern: /(pacing|rhythm|fast and slow|speed of|speed the|slow the)/i, focus: "pacing" },
  { pattern: /(emotion|emotional|feelings?|feeling|voice|perspective)/i, focus: "emotion" },
  {
    pattern: /(story structure|structure|beginning|middle|ending|climax|turning point|resolution|arc)/i,
    focus: "story structure",
  },
  { pattern: /(revise|revision|revising|edit|editing|feedback|before and after|before-after)/i, focus: "revision" },
];

function normalizeFocusText(value) {
  return String(value || "").replace(/\s+/g, " ").trim().toLowerCase();
}

function isCanonicalFocus(value) {
  return [
    "flow",
    "sensory detail",
    "dialogue",
    "pacing",
    "rhythm",
    "emotion",
    "story structure",
    "revision",
    "writing",
  ].includes(value);
}

function pickFocusFromText(text) {
  const candidate = normalizeFocusText(text);
  if (!candidate) return "";

  const match = FOCUS_PATTERNS.find(({ pattern }) => pattern.test(candidate));
  return match ? match.focus : "";
}

export function getLessonFocusLabel(lessonFocusOrLesson, lessonId = "") {
  if (typeof lessonFocusOrLesson === "string") {
    const direct = normalizeFocusText(lessonFocusOrLesson);
    if (direct) {
      const extracted = pickFocusFromText(direct);
      return extracted || (isCanonicalFocus(direct) ? direct : "writing");
    }
  }

  const lesson = lessonFocusOrLesson && typeof lessonFocusOrLesson === "object" ? lessonFocusOrLesson : {};
  const lessonMode = lesson.lesson_mode && typeof lesson.lesson_mode === "object" ? lesson.lesson_mode : {};
  const lessonIdText = normalizeFocusText(lessonId || lesson.id || lesson.lesson_id || "");

  if (lessonIdText) {
    const byId = LESSON_ID_FOCUS_FALLBACKS[lessonIdText.toUpperCase()];
    if (byId) return byId;
  }

  const candidates = [
    lesson.rubric_focus,
    lesson.focus,
    lesson.lesson_focus,
    lesson.learning_objective,
    lesson.objective,
    lesson.topic,
    lesson.task,
    lessonMode.feedback_style,
    lessonMode.mode,
  ];

  for (const candidate of candidates) {
    const extracted = pickFocusFromText(candidate);
    if (extracted) return extracted;
  }

  return "writing";
}

export function getPerformanceBand(score, lessonFocusOrLesson, lessonId = "") {
  const numericScore = Number(score);
  const band = PERFORMANCE_BANDS.find(({ min }) => Number.isFinite(numericScore) && numericScore >= min) || PERFORMANCE_BANDS[PERFORMANCE_BANDS.length - 1];
  const focus = getLessonFocusLabel(lessonFocusOrLesson, lessonId);

  return {
    band: band.label,
    focus,
    heading: `${band.label} ${focus}`.trim(),
    message: band.message,
  };
}
